<template>
  <EdroneTooltip
    v-if="tooltip"
    :disabled="disabled"
    :helper="tooltip"
    :offset="[0, 8]"
    :unique-id="uniqueId"
    position="top"
    trigger-event="mouseenter"
  >
    <div :class="[$options.name, classList]" :data-e-ga-id="uniqueId" v-bind="$attrs" @click.stop="onClick">
      <!--  @slot Use for text or icon -->
      <slot><font-awesome-icon icon="fa-solid fa-ban" /></slot>
    </div>
  </EdroneTooltip>
  <div v-else :class="[$options.name, classList]" :data-e-ga-id="uniqueId" v-bind="$attrs" @click.stop="onClick">
    <slot><font-awesome-icon icon="fa-solid fa-ban" /></slot>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import type { PropType } from "vue";
import EdroneTooltip from "./EdroneTooltip.vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneButtonIcon",
  components: {
    EdroneTooltip,
  },
  inheritAttrs: false,
  props: {
    type: {
      type: String as PropType<"border" | "link" | "plain" | "solid">,
      default: "border",
    },
    size: {
      type: String as PropType<"small" | "medium" | "big">,
      default: "big",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      default: "",
    },
    /**
     * Will be used when `type` is `link`
     */
    href: {
      type: String,
      default: "",
    },
    uniqueId: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * DEPRECATED
     * Use `type` instead
     */
    border: {
      type: Boolean,
      default: false,
    },
    /**
     * DEPRECATED
     * Use `href` instead
     */
    link: {
      type: Boolean,
      default: false,
    },
    /**
     * DEPRECATED
     * Use `size` instead
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * DEPRECATED
     * Use `size` instead
     */
    big: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click"],
  setup(props, { emit }) {
    const classList = computed(() => {
      return {
        "EdroneButtonIcon--border": props.type === "border" || props.border,
        "EdroneButtonIcon--plain": props.type === "plain",
        "EdroneButtonIcon--solid": props.type === "solid",
        "EdroneButtonIcon--link": (props.type === "link" || props.link) && (props.type !== "border" || !props.border),
        "EdroneButtonIcon--disabled": props.disabled,
        "EdroneButtonIcon--small": props.size === "small" || props.small,
        "EdroneButtonIcon--medium": props.size === "medium",
        "EdroneButtonIcon--big": props.size === "big" || props.big,
      };
    });

    function onClick() {
      if (props.disabled) {
        return;
      }
      if ((props.type === "link" || props.link) && props.href) {
        window.open(props.href, "_blank")?.focus();
        return;
      }
      emit("click");
      gtagUniversalSend({ component_id: props.uniqueId });
    }

    return {
      classList,
      onClick,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneButtonIcon {
  background: transparent;
  margin: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: all ease-in-out 300ms;
  cursor: pointer;
  user-select: none;
  @apply tw:text-black tw:rounded-sm tw:p-2 tw:text-base;
}

.EdroneButtonIcon:hover {
  @apply tw:bg-default-100;
}

.EdroneButtonIcon--small {
  @apply tw:text-[0.75rem] tw:p-1.5 tw:w-6 tw:h-6;
}

.EdroneButtonIcon--medium {
  @apply tw:text-base tw:p-2 tw:w-8 tw:h-8;
}

.EdroneButtonIcon--big {
  @apply tw:text-base tw:p-2 tw:w-10 tw:h-10;
}

.EdroneButtonIcon--border {
  background: transparent;
  @apply tw:border tw:border-default-200 tw:text-black;
}

.EdroneButtonIcon--border:hover {
  @apply tw:border tw:border-default-400 tw:bg-default-000 tw:text-black;
}

.EdroneButtonIcon--solid {
  @apply tw:text-black tw:bg-branding-500 tw:border tw:border-black tw:outline tw:outline-1 tw:outline-transparent;
}

.EdroneButtonIcon--solid:hover {
  @apply tw:outline-black tw:bg-branding-500;
}

.EdroneButtonIcon--link {
  background: transparent;
  @apply tw:border tw:border-transparent tw:text-blue-500;
}

.EdroneButtonIcon--link:hover {
  background: transparent;
  @apply tw:border tw:border-transparent tw:text-blue-600;
}

.EdroneButtonIcon--plain {
  @apply tw:border-transparent tw:border;
}

.EdroneButtonIcon--disabled {
  background: transparent;
  border-color: transparent;
  cursor: default;
  pointer-events: none;
  @apply tw:text-default-300;
}

.EdroneButtonIcon--disabled.EdroneButtonIcon--border {
  @apply tw:text-default-300 tw:bg-default-000 tw:border-default-000;
}

.EdroneButtonIcon--disabled.EdroneButtonIcon--link {
  background: transparent;
  border-color: transparent;
  @apply tw:text-blue-300;
}

.EdroneButtonIcon--disabled.EdroneButtonIcon--solid {
  @apply tw:bg-branding-000 tw:text-default-300 tw:border-default-200;
}
</style>
